import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { useEffect } from "react";

import { registerFcmToken, onFcmNavigate } from "./services/fcm";
import { isAuthenticated } from "./utils/session";

// PUBLIC COMPONENTS
import Home from "./components/Home";
import Login from "./components/Login";
import Register from "./components/Register";
import Terms from "./components/Terms";
import Privacy from "./components/Privacy";


// PROTECTED COMPONENTS
import AddBlog from "./components/AddBlog";
import ViewBlogs from "./components/ViewBlogs";
import Profile from "./components/Profile";
import Leaderboard from "./components/Leaderboard";
import AuthorProfile from "./components/AuthorProfile";
import AdminDashboard from "./components/AdminDashboard";

import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import Footer from "./components/Footer";
import AdvertisePage from "./components/AdvertisePage";
import AdSubmit from "./components/AdSubmit";
import AdvertiseWithUs from "./components/Ads/AdvertiseWithUs";
import AdminAdvertisingPage from "./components/Ads/AdminAdvertisingPage";
import TextAdsPage from "./components/TextAdsPage";
import ImageAdsPage from "./components/ImageAdsPage";
import InstallButton from "./components/InstallButton";

import VideoAds from "./components/VideoAds";
import AdsAnalytics from "./components/AdsAnalytics";
import LovePage from "./components/LovePage";
import LoveListPage from "./components/LoveListPage";
import AddYourName from "./components/AddYourName";
import CityLeaderboard from "./components/CityLeaderboard";
import AdminLove from "./components/AdminLove";
import BestBloggerToday from "./components/BestBloggerToday";
import AdminRewardPage from "./components/AdminRewardPage";
import RewardProof from "./components/RewardProof";
import SavedBlogs from "./components/SavedBlogs";
import UserProfileImage from "./components/UserProfileImage";
import SingleBlog from "./components/SingleBlog";
import UploadStatus from "./components/UploadStatus";
import ViewStatus from "./components/ViewStatus";
import Notifications from "./components/Notifications";
import AnnouncementDetail from "./components/AnnouncementDetail";
import Streak from "./components/Streak";
import Settings from "./components/Settings";
import Chat from "./components/Chat";
import PageGuard from "./components/PageGuard";
import AdsPage from "./components/AdsPage";
import CallProvider from "./components/chat/CallProvider";
import ActivityTracker from "./components/ActivityTracker";
import ActivityDashboard from "./components/ActivityDashboard";

// REFER & EARN
import ReferEarn from "./components/Referral/ReferEarn";
import AdminReferEarn from "./components/Referral/AdminReferEarn";
import InstallSignal from "./components/Referral/InstallSignal";
import SalesDashboard from "./components/SalesDashboard";
import AdminSalesExecutives from "./components/AdminSalesExecutives";
import EditBlog from "./components/EditBlog"; // ✅ CONFLICT FIX
import ErrorBoundary from "./components/ErrorBoundary"; // ✅ CONFLICT FIX
import BlueTickButton from "./components/BlueTickButton"; // ✅ CONFLICT FIX
import PremiumBadgeButton from "./components/PremiumBadgeButton"; // 
import TheBlogsSpherePro from "./components/Membership/TheBlogsSpherePro";

import ProPaymentSuccess from "./components/Membership/ProPaymentSuccess";

// THEBLOGSSPHERE PRO — HOME / CREATE CONTENT / MY PRO PROFILE
import ProRoute from "./components/Pro/ProRoute";
import ProHome from "./components/Pro/ProHome";
import ProCreateContent from "./components/Pro/ProCreateContent";
import ProProfile from "./components/Pro/ProProfile";

// THEBLOGSSPHERE PRO — PAID CREATOR SYSTEM
// Reader side (creator page, single blog / share preview, my access) and
// creator side (subscribers, earnings, bank, notifications), plus the admin
// panel. Pro blogs live in their own collection and never reach the free feed.
import ProCreatorProfile from "./components/Pro/ProCreatorProfile";
import ProBlogView from "./components/Pro/ProBlogView";
import ProEditBlog from "./components/Pro/ProEditBlog";
import ProAccessPage from "./components/Pro/ProAccessPage";
import ProUsers from "./components/Pro/ProUsers";
import ProEarnings from "./components/Pro/ProEarnings";
import ProBank from "./components/Pro/ProBank";
import ProNotifications from "./components/Pro/ProNotifications";
import AdminPro from "./components/Pro/AdminPro";

// BUY ME A TREAT — reader pages (choose, confirm, sent), blogger pages
// (received, payout setup) and the admin page
import MyReceivedTreats from "./components/Treat/MyReceivedTreats";
import MySentTreats from "./components/Treat/MySentTreats";
import TreatSelect from "./components/Treat/TreatSelect";
import TreatConfirm from "./components/Treat/TreatConfirm";
import TreatSent from "./components/Treat/TreatSent";
import TreatPayoutSetup from "./components/Treat/TreatPayoutSetup";
import AdminTreats from "./components/Treat/AdminTreats";

// SMART LINKS — admin Manage Links (links on every Nth blog card)
import AdminSmartLinks from "./components/SmartLinks/AdminSmartLinks";

// boost your blog
import BoostBlog from "./components/Boost/BoostBlog";
import BoostSuccess from "./components/Boost/BoostSuccess";
import BoostAnalytics from "./components/Boost/BoostAnalytics";
import ProPayment from "./components/ProPayment";
// FACE VERIFICATION (AWS Rekognition). Imported eagerly and safely: this file
// holds no AWS import. The AWS Face Liveness stage it runs lives in
// components/liveness/AwsFaceLiveness.jsx and is loaded with React.lazy from
// inside FaceVerification, so the AWS SDK sits in its own chunk that only a
// visitor on the face screen ever downloads.
import FaceVerification from "./components/FaceVerification";
// EMAIL VERIFICATION (Resend OTP). These two screens replace what Firebase
// Authentication used to do: the "Check Your Email / verification link" waiting
// page (gone entirely) and the Firebase password-reset link.
//     Register → VerifyOtp → FaceVerification → Login → Home
import VerifyOtp from "./components/VerifyOtp";
import ForgotPassword from "./components/ForgotPassword";
import Support from "./components/Support";
// SUPERSEDED: components/FaceAuth.jsx was the first attempt at Face Liveness —
// a standalone page with the pool id hardcoded and no link to an account. Its
// working parts now live in components/liveness/AwsFaceLiveness.jsx, wired into
// the real flow. It is left on disk as the reference it always was, and must
// stay unimported: it configures Amplify at module scope.
//
// (The note that used to be here claimed aws-amplify v6 cannot be bundled by
// react-scripts 5 at all. That was re-tested against the installed versions and
// is no longer true — it builds. What it does cost is ~300 kB gzip, which is
// why the liveness component is code-split rather than imported here.)

//membership



// ── FCM BRIDGE ──
function FcmBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated()) {
      registerFcmToken();
    }
    const unsubscribe = onFcmNavigate((url) => {
      if (!url) return;
      if (/^https?:\/\//i.test(url)) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(url);
    });
    return unsubscribe;
  }, [navigate]);

  return null;
}

function App() {
  const savedTheme = localStorage.getItem("theme") || "dark";
  document.body.className = savedTheme === 'dark'? 'dark-theme' : 'light-theme';

  return (
    <BrowserRouter>
      <FcmBridge />
      {/* App-wide, render-less activity tracker. No-ops until authenticated. */}
      <ActivityTracker />
      {/* App-wide, render-less Refer & Earn install reporter. Also no-ops until
          authenticated, and changes nothing about the Install button itself. */}
      <InstallSignal />
      <CallProvider>
        <ErrorBoundary>
          <Routes>
            {/* PUBLIC ROUTES */}
            <Route path="/" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
           <Route path="/pri" element={<ProPayment />} />

  {/* EMAIL VERIFICATION (Resend OTP) — public by design, for exactly the same
      reason the face route below is: it runs BEFORE the account is allowed to
      log in, so the visitor has no session yet and ProtectedRoute would bounce
      them back to the login screen they are being kept out of. The account is
      identified by the registered email instead, and the screen can decide
      nothing on its own — the server owns every check. */}
  <Route path="/verify-otp" element={<VerifyOtp />} />

  {/* FORGOT PASSWORD — email → OTP → new password → confirm. Replaces the
      Firebase reset-link modal that used to live on the login screen. */}
  <Route path="/forgot-password" element={<ForgotPassword />} />

  {/* FACE VERIFICATION — public by design. It sits BETWEEN email verification
      and login, so the visitor has no session yet and ProtectedRoute would
      bounce them straight back to the login screen they are being kept out of.
      The account is identified by the registered email instead. */}
  <Route path="/face-verification" element={<FaceVerification />} />
  {/* No /face route: FaceAuth is superseded — see the note by the import. */}


//membership

<Route
  path="/theblogssphere-pro"
  element={<TheBlogsSpherePro />}
/>



<Route
  path="/theblogssphere-pro/payment-success"
  element={<ProPaymentSuccess />}
/>

{/* PRO HOME / CREATE CONTENT / MY PRO PROFILE — active Pro membership only.
    ProtectedRoute proves a session; ProRoute asks the server for the
    membership. The server refuses the Pro APIs either way. */}
<Route
  path="/pro-home"
  element={<ProtectedRoute><ProRoute><ProHome /></ProRoute></ProtectedRoute>}
/>

<Route
  path="/create-content"
  element={<ProtectedRoute><ProRoute><ProCreateContent /></ProRoute></ProtectedRoute>}
/>

<Route
  path="/pro-profile"
  element={<ProtectedRoute><ProRoute><ProProfile /></ProRoute></ProtectedRoute>}
/>

{/* ── PRO CREATOR DASHBOARD ──
    These need a SESSION but not an active ₹29 membership: a creator whose
    membership lapsed must still be able to see the money they already earned
    and to receive the payout for it. Publishing new Pro content is what the
    membership gates, and that guard is on /create-content above. The server
    scopes every one of these to the caller's own id regardless. */}
<Route
  path="/pro-users"
  element={<ProtectedRoute><ProUsers /></ProtectedRoute>}
/>

<Route
  path="/pro-earnings"
  element={<ProtectedRoute><ProEarnings /></ProtectedRoute>}
/>

<Route
  path="/pro-bank"
  element={<ProtectedRoute><ProBank /></ProtectedRoute>}
/>

<Route
  path="/pro-notifications"
  element={<ProtectedRoute><ProNotifications /></ProtectedRoute>}
/>

{/* ── PRO READER SIDE ──
    A reader who pays ₹19 for a creator does NOT need a Pro membership, so
    these are open to any signed-in account. */}
<Route
  path="/pro-creator/:creatorId"
  element={<ProtectedRoute><ProCreatorProfile /></ProtectedRoute>}
/>

<Route
  path="/pro-access"
  element={<ProtectedRoute><ProAccessPage /></ProtectedRoute>}
/>

{/* SECURE SHARE PREVIEW (Step 14) — deliberately PUBLIC.
    A share link has to open for someone who is not signed in; they see the
    locked preview and are asked to sign in and pay. The paywall is applied by
    the server per request, so a public route leaks nothing: a locked blog's
    body, images and video URL are never in the response. */}
<Route path="/pro-blog/:blogId" element={<ProBlogView />} />

{/* EDIT / DELETE MY OWN PRO BLOG — the server refuses both unless the
    signed-in account is the blog's author, so this route is convenience,
    not the security boundary. Declared AFTER the view route; the extra
    "/edit" segment is what distinguishes them. */}
<Route
  path="/pro-blog/:blogId/edit"
  element={<ProtectedRoute><ProEditBlog /></ProtectedRoute>}
/>

            {/* PROTECTED ROUTES */}
            <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/pro" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/blogs" element={<ProtectedRoute><ViewBlogs /></ProtectedRoute>} />
            <Route path="/add-blog" element={<ProtectedRoute><AddBlog /></ProtectedRoute>} />
            <Route path="/blog/:id" element={<SingleBlog />} />
            <Route path="/edit-blog/:id" element={<EditBlog />} />
            <Route path="/authorprofile/:author" element={<ProtectedRoute><AuthorProfile /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
            <Route path="/today" element={<ProtectedRoute><BestBloggerToday /></ProtectedRoute>} />
            <Route path="/save" element={<ProtectedRoute><SavedBlogs /></ProtectedRoute>} />
            <Route path="/streak" element={<Streak />} />
            <Route path="/set" element={<Settings />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/chat/:receiverName" element={<Chat />} />
            <Route path="/notify" element={<Notifications />} />
            <Route path="/announcement/:id" element={<AnnouncementDetail />} />
            <Route path="/upload-status" element={<UploadStatus />} />
            <Route path="/view-status" element={<ViewStatus />} />
            <Route path="/img" element={<UserProfileImage />} />
            <Route path="/ads" element={<AdsPage />} />

          
            <Route path="/act" element={<ActivityDashboard />} />
            <Route path="/in" element={<ProtectedRoute><InstallButton /></ProtectedRoute>} />
            <Route path="/fo" element={<ProtectedRoute><Footer /></ProtectedRoute>} />

            {/* BOOST YOUR BLOG ROUTES */}
            <Route path="/boost/:blogId" element={<ProtectedRoute><BoostBlog /></ProtectedRoute>} />
            <Route path="/boost/:blogId/success" element={<ProtectedRoute><BoostSuccess /></ProtectedRoute>} />
            <Route path="/boost/:blogId/analytics" element={<ProtectedRoute><BoostAnalytics /></ProtectedRoute>} />

            {/* PREMIUM & PAYMENT ROUTES */}
            <Route path="/blue" element={<ProtectedRoute><BlueTickButton /></ProtectedRoute>} /> 
            <Route path="/premium" element={<ProtectedRoute><PremiumBadgeButton /></ProtectedRoute>} /> {/* ✅ NEW */}

            {/* LOVE ROUTES */}
            <Route path="/lo" element={<ProtectedRoute><LovePage /></ProtectedRoute>} />
            <Route path="/lovelist" element={<ProtectedRoute><LoveListPage /></ProtectedRoute>} />
            <Route path="/loveadd" element={<ProtectedRoute><AddYourName /></ProtectedRoute>} />
            {/* LIVE CITY LEADERBOARD — "Take Your City First". Protected like
                every other Love route, so the CTA on the Live Leaderboard never
                lands a signed-in user on the login screen. */}
            <Route path="/city-leaderboard" element={<ProtectedRoute><CityLeaderboard /></ProtectedRoute>} />
            <Route path="/adminlove" element={<ProtectedRoute><AdminLove /></ProtectedRoute>} />

            {/* AD ROUTES */}
            <Route path="/ad" element={<ProtectedRoute><AdvertisePage /></ProtectedRoute>} />
            <Route path="/sub" element={<ProtectedRoute><AdSubmit /></ProtectedRoute>} />
            {/* ADVERTISE WITH US — create, pay for and track paid campaigns */}
            <Route path="/advertise" element={<ProtectedRoute><AdvertiseWithUs /></ProtectedRoute>} />

            {/* REWARD ROUTES */}
            <Route path="/pr" element={<ProtectedRoute><RewardProof /></ProtectedRoute>} />
             <Route path="/support" element={<ProtectedRoute>< Support /></ProtectedRoute>} />

            {/* ADMIN ROUTES */}
            <Route path="/admin" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
            <Route path="/re" element={<ProtectedAdminRoute><AdminRewardPage /></ProtectedAdminRoute>} />
            <Route path="/activity" element={<ProtectedAdminRoute><ActivityDashboard /></ProtectedAdminRoute>} />
            <Route path="/tx" element={<ProtectedAdminRoute><TextAdsPage /></ProtectedAdminRoute>} />
            <Route path="/ix" element={<ProtectedAdminRoute><ImageAdsPage /></ProtectedAdminRoute>} />
            <Route path="/vi" element={<ProtectedAdminRoute><VideoAds /></ProtectedAdminRoute>} />
            <Route path="/ana" element={<ProtectedAdminRoute><AdsAnalytics /></ProtectedAdminRoute>} />
            <Route path="/sales-admin" element={<ProtectedAdminRoute><AdminSalesExecutives /></ProtectedAdminRoute>} />
            {/* PRO ADMIN PANEL (Steps 26, 27) — Pro Users, Pro Creators,
                Payments & Earnings, Payouts, Transactions. */}
            <Route path="/admin-pro" element={<ProtectedAdminRoute><AdminPro /></ProtectedAdminRoute>} />
            <Route path="/ads-admin" element={<ProtectedAdminRoute><AdminAdvertisingPage /></ProtectedAdminRoute>} />

            {/* SALES ROUTES */}
            <Route path="/sales" element={<ProtectedRoute><SalesDashboard /></ProtectedRoute>} />

            {/* REFER & EARN */}
            <Route path="/refer" element={<ProtectedRoute><ReferEarn /></ProtectedRoute>} />
            <Route path="/refer-admin" element={<ProtectedAdminRoute><AdminReferEarn /></ProtectedAdminRoute>} />

            {/* BUY ME A TREAT */}
            <Route path="/buy-treat/:blogId" element={<ProtectedRoute><TreatSelect /></ProtectedRoute>} />
            <Route path="/buy-treat/:blogId/confirm" element={<ProtectedRoute><TreatConfirm /></ProtectedRoute>} />
            <Route path="/treat-sent/:treatId" element={<ProtectedRoute><TreatSent /></ProtectedRoute>} />
            <Route path="/my-sent-treats" element={<ProtectedRoute><MySentTreats /></ProtectedRoute>} />
            <Route path="/my-treats" element={<ProtectedRoute><MyReceivedTreats /></ProtectedRoute>} />
            <Route path="/treat-payouts" element={<ProtectedRoute><TreatPayoutSetup /></ProtectedRoute>} />
            <Route path="/admin-treats" element={<ProtectedAdminRoute><AdminTreats /></ProtectedAdminRoute>} />

            {/* SMART LINKS */}
            <Route path="/smart-links-admin" element={<ProtectedAdminRoute><AdminSmartLinks /></ProtectedAdminRoute>} />

          </Routes>
        </ErrorBoundary>
      </CallProvider>
    </BrowserRouter>
  );
}

export default App;